import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { DNode } from '@dojo/framework/core/interfaces';
import Button from '@dojo/widgets/button';
import { formatAriaProperties } from '@dojo/widgets/common/util';

import { numeral } from '@holmescorp/numeral';

import { DateTime } from 'luxon';
import timesync from '@holmescorp/timesync';

import { logQuiz } from '@holmescorp/logger';
import { mpeChange, mpeRemove, userstore } from '@holmescorp/lcc-state';

import { BaseProgression } from './base';
import { ScoreRollup } from '../support/score-rollup';
import { QuestionListMPE } from '../support/questionlist-mpe';
import { Countdown } from '../support/countdown-timer';
import { CountdownTimer } from '../widgets/countdown-timer';
import { svc as prometricsvc } from '../support/prometricmpe-svc';
import { svc as auditsvc } from '../support/audit-svc';
import { IQuestionFeedback } from '../support/qfsvc';
import {
	quizChange,
	infoChange,
	setTitle,
	setInreview,
	setIsaudit,
	setQuestion,
	setProgressionIsrunning,
	setActiveQuizSection
} from '../support/state';
import { delay } from '../support/delay';
import { QuizRPC, QuestionQueueType } from '../interfaces';

import css from '../css/prometric.m.css';
import { convertToBoolean } from '@holmescorp/common/convertToBoolean';

const isEmpty = (obj: any) => Object.keys(obj).length === 0;

/*
Are you sure you want to submit this section of the exam?  You will not be able to
return to this section once you submit.
*/

@theme(css)
export class PrometricMPE extends BaseProgression {
	protected svc: IQuestionFeedback = prometricsvc;
	protected auditsvc = auditsvc;
	private countdown: Countdown | null = null;
	private isloading: boolean = false;
	private changeHnd: { remove: () => void } | null = null;

	protected onDetach(): void {
		this.countdown && this.countdown.stop();
		this.countdown = null;
		this.changeHnd && this.changeHnd.remove();
		this.changeHnd = null;
	}

	private getBufferMinutes(): number {
		const { quizstore } = this.properties;
		const { get, path } = quizstore;
		const activeSection = get(path('activeSection'));
		return (activeSection && activeSection.section && activeSection.section.mpe_buffer) || 60;
	}

	private handleBufferExpired(): void {
		const { quizstore } = this.properties;
		const { get, path } = quizstore;
		const quiz = get(path('quiz'));
		this.countdown && this.countdown.stop();
		this.countdown = null;
		mpeRemove({ quizid: quiz.id });
		this.svc.finish_quiz().then(() => {
			this.finalize();
		});
	}

	protected async load_quiz(): Promise<boolean> {
		const { quizurl = '', quizstore } = this.properties;
		const bufferMinutes = this.getBufferMinutes();
		const { svc, auditsvc } = this;
		svc._smd!.url = quizurl;
		auditsvc._smd!.url = quizurl;
		if (this.isloading) {
			return true;
		}

		if (this.changeHnd === null) {
			this.changeHnd = quizstore.onChange(quizstore.path('activeSection'), () => {
				const { get, path } = quizstore;
				const activeSection = get(path('activeSection'));
				const now = DateTime.fromJSDate(timesync.now()).setZone('utc');
				const timeout = activeSection.timeout;
				this.countdown && this.countdown.stop();
				this.countdown = new Countdown(timeout, quizstore, this.getBufferMinutes(), () =>
					this.handleBufferExpired()
				);
				this.countdown.start();
				const quiz = get(path('quiz'));
				const quizid = quiz.id;
				const duration = this.countdown.duration;
				mpeChange({
					quizid,
					item: {
						sectioncursor: activeSection.cursor,
						timeout: now.plus(duration).toISO(),
						inreview: false
					}
				});
				this.invalidate();
			});
		}
		this.isloading = true;
		setProgressionIsrunning(quizstore)({ isrunning: '' });
		const [quiz, info] = await Promise.all([svc.quiz(), svc.info()]);
		const { title } = quiz;
		setTitle(quizstore)({ title });

		// Check if the user has exceeded the buffer past the countdown reaching zero.
		// The saved mpe timeout is the absolute finish time (ISO string).
		// If now > finishTime + bufferMinutes, the quiz is over.
		const isExpiredPastBuffer = (): boolean => {
			if (quiz.use_restart) {
				const mpe = userstore.get(userstore.path('mpe'));
				if (mpe && mpe[quiz.id]) {
					const finish = mpe[quiz.id]!.timeout;
					if (finish !== '') {
						const sectionfinish = DateTime.fromISO(finish);
						const now = DateTime.fromJSDate(timesync.now()).setZone('utc');
						const elapsed = now.diff(sectionfinish, 'minutes').minutes;
						return elapsed >= bufferMinutes;
					}
				}
			}
			return false;
		};

		if (isExpiredPastBuffer()) {
			this.countdown && this.countdown.stop();
			this.countdown = null;
			mpeRemove({ quizid: quiz.id });
			await svc.finish_quiz();
			await this.finalize();
			await delay(500);
			this.isloading = false;
			this.load_quiz();
		} else {
			quizChange(quizstore)({ quiz });
			infoChange(quizstore)({ info });
			const getTimeout = () => {
				if (quiz.use_restart) {
					const quizid = quiz.id;
					const mpe = userstore.get(userstore.path('mpe'));
					if (mpe && mpe[quizid]) {
						const finish = mpe[quizid]!.timeout;
						if (finish !== '') {
							const sectionfinish = DateTime.fromISO(finish);
							const now = DateTime.fromJSDate(timesync.now()).setZone('utc');
							const timeout = sectionfinish.diff(now, ['hours', 'minutes', 'seconds']).toObject();
							const { get, path } = quizstore;
							const activeSection = get(path('activeSection'));
							activeSection.timeout = timeout;
							setActiveQuizSection(quizstore)(activeSection);
						}
					}
				}
			};
			getTimeout();
		}
		this.isloading = false;
		return true;
	}

	protected audit_startup(): Promise<boolean> {
		const {
			localizedBundle: {
				messages: { audit: auditText = 'Review' }
			},
			quiz,
			quizstore
		} = this.properties;

		setProgressionIsrunning(quizstore)({ isrunning: 'audit' });
		setTitle(quizstore)({ title: `${quiz ? quiz.title : ''} ${auditText}` });
		setQuestion(quizstore)({ question: undefined });
		this.invalidate();

		return Promise.resolve(true);
	}

	protected buttons(): DNode[] {
		const {
			localizedBundle: {
				messages: {
					continue: continueText = '',
					begin: beginText = '',
					review_last_attempt: reviewText = '',
					hide_begin_on_quiz_end = 'none',
					show_practice_timer = 'true'
				}
			},
			quiz = {} as QuizRPC,
			quizid,
			quizstore,
			info,
			isrunning,
			theme
		} = this.properties;
		const { use_restart } = quiz;
		const { svc } = this;
		const noBeginOnFinal = isrunning === 'final' && hide_begin_on_quiz_end !== 'none';
		const showTimer = convertToBoolean(show_practice_timer);

		if (use_restart) {
			if (this.countdown === null) {
				const getTimeout = () => {
					const mpe = userstore.get(userstore.path('mpe'));
					if (mpe && mpe[quizid]) {
						const finish = mpe[quizid]!.timeout;
						if (finish !== '') {
							const sectionfinish = DateTime.fromISO(finish);
							const now = DateTime.fromJSDate(timesync.now()).setZone('utc');
							const timeout = sectionfinish.diff(now, ['hours', 'minutes', 'seconds']).toObject();
							const { get, path } = quizstore;
							const activeSection = get(path('activeSection'));
							activeSection.timeout = timeout;
							setActiveQuizSection(quizstore)(activeSection);
							return timeout;
						}
					}
					const { get, path } = quizstore;
					return get(path('activeSection', 'timeout'));
				};
				this.countdown = new Countdown(getTimeout(), quizstore, this.getBufferMinutes(), () =>
					this.handleBufferExpired()
				);
			}
			this.countdown && this.countdown.start();

			return [
				v('div', { xqa: 'action continue', classes: [this.theme(css.oneaction)] }, [
					w(
						Button,
						{
							theme,
							key: 'continue',
							onClick: () => {
								if (!this._inAction) {
									this._inAction = true;
									logQuiz.info(`continue quiz ${quizid}`);
									setInreview(quizstore)({ inreview: false });
									svc.continue_quiz().then((v) => {
										this.startquiz = v;
										this.quiz_startup();
									});
								}
							}
						},
						[continueText]
					),
					showTimer
						? v('div', { key: 'timer', classes: [this.theme(css.timerblock)] }, [
								v('div', { classes: [this.theme(css.timer)] }, [
									w(CountdownTimer, { quizstore, countdown: '' })
								])
						  ])
						: null
				])
			];
		} else {
			const lastresult = info && info.lastresult;
			const hasLastresult = lastresult && !isEmpty(lastresult);
			return [
				v('div', { xqa: 'action begin', classes: [this.theme(css.oneaction)] }, [
					noBeginOnFinal
						? null
						: w(
								Button,
								{
									theme,
									key: 'begin',
									onClick: () => {
										if (!this._inAction) {
											this._inAction = true;
											logQuiz.info(`begin quiz ${quizid}`);
											this.countdown && this.countdown.stop();
											this.countdown = null;
											setInreview(quizstore)({ inreview: false });
											svc.start_quiz().then((v) => {
												this.startquiz = v;
												this.quiz_startup();
											});
										}
									}
								},
								[beginText]
						  )
				]),
				hasLastresult && !quiz.attempts_noaudit
					? w(
							Button,
							{
								theme,
								key: 'audit',
								onClick: () => {
									if (!this._inAction) {
										this._inAction = true;
										const { auditsvc } = this;
										logQuiz.info(`audit quiz ${quizid}`);

										setIsaudit(quizstore)({ isaudit: true });
										setInreview(quizstore)({ inreview: false });

										auditsvc.start_quiz().then(() => {
											this.startquiz = { cursor: 0, status: 'feedback' };
											this.audit_startup();
										});
									}
								}
							},
							[reviewText]
					  )
					: null
			];
		}
	}

	protected render_page(): DNode[] {
		const { isPlaceholder, messages } = this.properties.localizedBundle;
		if (isPlaceholder) {
			return [];
		}
		const scoreText = messages['score'] || '';
		const lastScoreText = messages['last_score'] || '';
		const { quiz = {} as QuizRPC, info, isrunning, key, instancekey = '', quizstore } = this.properties;
		if (isrunning === 'audit') {
			const { startquiz, auditsvc } = this;
			const resultid = info && info.lastresult.id;
			const finalize = () => {
				setIsaudit(quizstore)({ isaudit: false });
				setInreview(quizstore)({ inreview: false });
				setTitle(quizstore)({ title: quiz.title });
				setProgressionIsrunning(quizstore)({ isrunning: '' });
				this.invalidate();
			};
			return [
				w<QuestionQueueType>(QuestionListMPE, {
					key: `${instancekey}-queue`,
					svc: auditsvc as any,
					finalize,
					startquiz,
					resultid,
					quizstore
				})
			];
		} else if (isrunning === 'quiz') {
			if (this.countdown === null) {
				const { get, path } = quizstore;
				const countdowntime = get(path('activeSection', 'timeout'));
				this.countdown = new Countdown(countdowntime, quizstore, this.getBufferMinutes(), () =>
					this.handleBufferExpired()
				);
			}
			this.countdown && this.countdown.start();
			const { startquiz, svc } = this;
			const finalize = () => {
				this.countdown && this.countdown.stop();
				this.countdown = null;
				mpeRemove({ quizid: quiz.id });
				this.finalize();
			};
			return [w<QuestionQueueType>(QuestionListMPE, { svc, finalize, startquiz, quizstore })];
		} else if (isrunning === 'final') {
			this.countdown && this.countdown.stop();
			this.countdown = null;

			const lastresult = info && info.lastresult;
			const hasLastresult = lastresult && !isEmpty(lastresult);
			const percent = hasLastresult ? lastresult!.totalpercentcorrect * 0.01 : 0;
			const { instructions_done } = quiz;
			return [
				hasLastresult ? w('QuizModuleProgress', { key: `${key}-progress`, quizstore }) : null,
				v('div', {
					key: `${key}-instructions`,
					id: 'quiz-instructions',
					classes: [this.theme(css.instructions)],
					innerHTML: instructions_done
				}),
				hasLastresult && !quiz.attempts_noscore
					? v('div', { key: 'score', classes: [this.theme(css.score)], xqa: 'last-quiz-score' }, [
							v('span', { key: 's0', classes: [this.theme(css.scoreTitle)] }, [`${scoreText} `]),
							v('span', { key: 's1', classes: [this.theme(css.scorePercent)] }, [
								`${numeral(percent).format('0%')}`
							])
					  ])
					: v('div', { key: 'score', xqa: 'last-quiz-score' }),
				v(
					'div',
					{
						key: 'quiz-actions',
						classes: [this.theme(css.actions)],
						role: 'group',
						...formatAriaProperties({ labelledby: 'quiz-instructions' })
					},
					this.buttons()
				)
			];
		} else {
			const { info } = this.properties;
			const lastresult = info && info.lastresult;
			const hasLastresult = lastresult && !isEmpty(lastresult);
			const percent = hasLastresult ? lastresult!.totalpercentcorrect * 0.01 : 0;
			const { instructions } = quiz;
			return [
				hasLastresult ? w('QuizModuleProgress', { quizstore }) : null,
				v('div', {
					key: 'instructions',
					id: 'quiz-instructions',
					classes: [this.theme(css.instructions)],
					innerHTML: instructions
				}),
				hasLastresult && !quiz.attempts_noscore
					? v('div', { key: 'score', classes: [this.theme(css.score)], xqa: 'last-quiz-score' }, [
							v('span', { key: 's0', classes: [this.theme(css.scoreTitle)] }, [`${lastScoreText} `]),
							v('span', { key: 's1', classes: [this.theme(css.scorePercent)] }, [
								`${numeral(percent).format('0%')}`
							])
					  ])
					: v('div', { key: 'score', xqa: 'last-quiz-score' }),
				v(
					'div',
					{
						key: 'quiz-actions',
						classes: [this.theme(css.actions)],
						role: 'group',
						...formatAriaProperties({ labelledby: 'quiz-instructions' })
					},
					this.buttons()
				)
			];
		}
	}

	protected render(): DNode {
		const { quiz, info, key, quizstore } = this.properties;
		const hasdata = quiz && info;
		!hasdata && this.load_quiz();

		return v(
			'div',
			{ key, xqa: `progression prometricmpe` },
			hasdata
				? [w(ScoreRollup, { key: `rollup-${key}`, quizstore } as any, this.render_page())]
				: this.render_loading()
		);
	}
}

export default PrometricMPE;
